export const MINI_PROJECT_DETAILS = [
  {
    id: 'p1',
    code: 'P1',
    name: 'PromptForge',
    tagline: 'Structured-output playground',
    skill: 'Prompt & context engineering, structured outputs',
    whatItIs: 'A tool that takes messy text — emails, invoices, support tickets, résumés — and returns clean, validated JSON in a fixed format, plus a playground for testing prompts.',
    problem: "Most business AI starts with 'extract structured data reliably'. Raw model output is inconsistent; production systems need a guaranteed shape.",
    build: 'Prompt and context engineering, JSON-mode outputs, a schema (Pydantic) that validates every response, automatic retries when the model gets it wrong.',
    proof: 'Extraction accuracy against a labelled set; % of responses that pass validation first time.',
    why: 'The first thing you learn is to never trust raw model output — every response is validated against a contract. That is what separates a demo from a product.',
  },
  {
    id: 'p2',
    code: 'P2',
    name: 'ModelSwitch',
    tagline: 'Multi-model router + first eval harness',
    skill: 'Model routing, first evals, cost/latency',
    whatItIs: 'A router that sends each request to Claude, Gemini, OpenAI or a local open model (via Ollama), and a harness that scores every model on quality, speed, tokens and cost.',
    problem: 'Companies over-spend by sending everything to the biggest model. They want to choose models on evidence, not hype.',
    build: 'Multi-provider access behind one gateway, routing rules, and the first evaluation harness — a reusable habit for the whole program.',
    proof: 'A comparison table: quality vs latency vs cost per model on the same tasks.',
    why: 'By week 3 you can tell a CTO which model to use for which task — with numbers.',
  },
  {
    id: 'p3',
    code: 'P3',
    name: 'SnapClassify',
    tagline: 'Deployed image classifier',
    skill: 'Deep-learning & computer-vision basics',
    whatItIs: 'An image classifier built with transfer learning in PyTorch, deployed as a small web app, then compared with a multimodal LLM on the same task.',
    problem: 'Not every problem needs an LLM. Teams need engineers who know when a small classical model is cheaper, faster and better.',
    build: 'Deep-learning and computer-vision basics (CNNs, transfer learning), model deployment, a classical-model-vs-LLM comparison.',
    proof: 'Accuracy, inference speed and cost of the classifier vs the multimodal LLM.',
    why: "A computer-vision primer is built in, so you don't graduate as a one-trick LLM engineer.",
  },
  {
    id: 'p4',
    code: 'P4',
    name: 'FindIt',
    tagline: 'Hybrid search with a reranker',
    skill: 'Retrieval engineering',
    whatItIs: 'A search engine over a real dataset (e.g. a product catalogue) that combines keyword search and semantic (meaning-based) search, fuses the results, then re-ranks them.',
    problem: 'Pure semantic search misses exact terms (part numbers, names); pure keyword search misses meaning. Real systems need both.',
    build: 'Embeddings, sparse/keyword retrieval, Reciprocal Rank Fusion, a cross-encoder reranker — and you measure the gain from each step.',
    proof: 'Recall@k before and after each retrieval improvement.',
    why: 'Retrieval quality makes or breaks a RAG system. You learn it as engineering, with measurements — not as a library call.',
  },
  {
    id: 'p5',
    code: 'P5',
    name: 'ToolAgent',
    tagline: 'ReAct agent on a live API',
    skill: 'Tool calling, the agent loop',
    whatItIs: 'The minimum viable agent: a model that reasons, calls a real external API (e.g. GitHub, weather, a helpdesk), looks at the result and decides the next step.',
    problem: 'Chatbots answer; agents act. Companies want systems that can look things up and take steps, not just talk.',
    build: 'Tool/function calling, the reason → act → observe loop, error handling and retries, basic guardrails.',
    proof: 'Task-completion rate and tool-call accuracy on a set of tasks.',
    why: 'This is where you move from prompting a model to building something that does work.',
  },
  {
    id: 'p6',
    code: 'P6',
    name: 'GateKeeper MCP',
    tagline: 'Multi-tenant MCP server with RBAC & audit',
    skill: 'MCP, permissions (RBAC), audit logs',
    whatItIs: 'A server built on the Model Context Protocol (MCP) — the standard way to give AI agents access to tools and data — with per-customer isolation, role-based permissions, OAuth and audit logs.',
    problem: 'Enterprises will not connect agents to internal systems without permissions, compliance literacy and an audit trail. This is the #1 blocker to agent adoption.',
    build: 'An MCP server exposing real tools, multi-tenant scoping, RBAC at the tool boundary, audit logging — reused later by TriageOps.',
    proof: 'Permission tests (tenant A cannot see tenant B), a complete audit trail for every tool call.',
    why: 'Most courses show MCP as a demo. You build the enterprise-safe version — the one a security team would approve.',
  },
  {
    id: 'p7',
    code: 'P7',
    name: 'EvalLab',
    tagline: 'LLM-as-judge + regression set as a CI gate',
    skill: 'Evaluation-driven development, CI gates',
    whatItIs: "An automated quality gate: a set of test cases plus an AI 'judge' that scores outputs, wired into the release pipeline so a change that lowers quality is blocked automatically.",
    problem: 'AI systems silently regress when prompts or models change. Teams need the equivalent of unit tests for AI.',
    build: 'Evaluation sets built from real failures, an LLM-as-judge with a calibrated rubric, regression testing in CI (GitHub Actions) that catches a planted regression.',
    proof: 'The gate blocks the planted regression; judge agreement with human labels.',
    why: 'Evaluation is the spine of the program — every later build ships with evals in CI. Hiring managers ask about this in every interview.',
  },
  {
    id: 'p8',
    code: 'P8',
    name: 'TinyTune',
    tagline: 'LoRA/QLoRA fine-tune + distillation',
    skill: 'Fine-tuning, quantisation, distillation',
    whatItIs: 'A small open model fine-tuned for a narrow task using efficient methods (LoRA/QLoRA) on a single GPU, evaluated before and after, then distilled into an even smaller model.',
    problem: 'Frontier APIs can be too costly, too slow or not private enough. Companies need engineers who know when fine-tuning pays and how to do it cheaply.',
    build: 'Dataset preparation, 4-bit quantisation, LoRA/QLoRA training with Hugging Face, before/after evaluation, a distillation demo.',
    proof: 'Quality gain vs the base model; cost and latency vs a frontier API.',
    why: "You don't just call APIs — you can customise and shrink models when the economics demand it.",
  },
  {
    id: 'p9',
    code: 'P9',
    name: 'RouteCache',
    tagline: 'Model router + semantic-cache gateway',
    skill: 'Caching, routing, cost engineering',
    whatItIs: 'A gateway in front of the LLM providers that routes each request to the cheapest capable model and answers repeated questions from a semantic cache (Redis) instead of calling the model again.',
    problem: 'LLM bills and latency grow with usage. Routing and caching are the two biggest levers, often cutting cost 30–70% — you measure your own number.',
    build: 'Routing rules, semantic + prompt caching, token budgets, a cost-per-query dashboard.',
    proof: 'Cost and latency before vs after, on the same traffic.',
    why: 'Cost engineering is a job requirement now. You leave with a dashboard showing the savings you achieved.',
  },
  {
    id: 'p10',
    code: 'P10',
    name: 'SafeOps',
    tagline: 'Observability + guardrails + drift alert',
    skill: 'Observability, security, guardrails',
    whatItIs: 'Production safety added to an earlier build: end-to-end tracing of every request (Langfuse), automatic PII redaction (Presidio), a demonstrably blocked prompt-injection attack, and an alert when quality drifts.',
    problem: 'Security and compliance teams ask: what data went to the model, was PII protected, can it be manipulated, will we know when it degrades?',
    build: 'Tracing, PII redaction, injection defence, drift detection and alerting (Prometheus/Grafana).',
    proof: 'A trace for every request, redaction tests, a blocked attack in the logs, a fired drift alert.',
    why: "You can answer a CISO's questions about an AI system — with evidence.",
  },
];

export const GUIDED_PROJECT_DETAILS = [
  {
    id: 'b1',
    code: 'B1',
    name: 'AskDocs',
    tagline: 'Workspace-intelligence assistant over real documents',
    skill: 'Production RAG end-to-end',
    whatItIs: "A 'chat with your company documents' assistant connected to a real source (Notion or Google Drive) that gives grounded, cited answers and remembers the conversation.",
    problem: 'Teams cannot find answers across scattered docs; generic chatbots make things up. Answers must be trustworthy and traceable to a source.',
    build: 'Ingestion with incremental sync → chunking → hybrid retrieval + reranking → cited answers → conversation memory → guardrails for off-topic and injection; an agentic-RAG extension for multi-step questions.',
    proof: 'A golden Q&A set; recall/precision, faithfulness (does the answer match the source?), answer quality by LLM-judge; latency and cost per query; evals run in CI.',
    ships: 'Live assistant + eval report + cost/latency dashboard.',
    why: 'This is the most requested enterprise AI pattern — and yours is built over real Notion/Drive data with citations and measurable accuracy, not a PDF demo.',
  },
  {
    id: 'b2',
    code: 'B2',
    name: 'TriageOps',
    tagline: 'Support-operations agent over a real helpdesk',
    skill: 'Enterprise agents, MCP, human-in-the-loop',
    whatItIs: 'A multi-agent system running on a real helpdesk: a supervisor agent routes each ticket to a classifier, a knowledge-grounded responder and an escalation agent; humans approve every action that changes data.',
    problem: 'Helpdesks drown in tickets. Automating replies is only acceptable if they are grounded, safe, auditable and reviewable.',
    build: 'Supervisor + specialist agents, tools exposed through your own GateKeeper MCP server (permissions + audit), human-in-the-loop approval, agent evaluation, full hardening (tracing, guardrails, PII redaction, cost per ticket).',
    proof: 'Classification accuracy, deflection rate, draft-reply quality, tool-call accuracy, escalation precision, cost per ticket.',
    ships: 'Deployed agent + trace/eval console.',
    stretch: 'Hand-off to a billing agent; multilingual replies.',
    why: 'An enterprise agent the way enterprises actually deploy them — permissions, audit trail, human approval, measured deflection.',
  },
  {
    id: 'b3',
    code: 'B3',
    name: 'DomainCopilot',
    tagline: 'Fine-tuned vertical copilot (fintech / health / legal)',
    skill: 'Fine-tuning + RAG + cost/quality analysis',
    whatItIs: 'An industry-specific assistant built on a small open model that you fine-tune, ground with RAG, and benchmark head-to-head against a frontier API on quality, speed and cost.',
    problem: 'For high-volume or sensitive domains, frontier APIs can be too expensive or too generic. Companies want to know if a customised small model can match them at a fraction of the cost.',
    build: 'Domain dataset curation → QLoRA fine-tune → publish the adapter to a model hub → serve it (vLLM/Ollama) behind RAG → side-by-side evaluation → a model card with data provenance and PII handling.',
    proof: 'Win-rate vs the base model (judge + human sample), faithfulness with RAG, latency, cost per 1,000 queries vs a frontier API.',
    ships: 'Adapter on the hub + app + eval report + trade-off note.',
    stretch: 'Preference tuning (DPO); distil to an even smaller model.',
    why: 'You can walk into a cost-vs-quality conversation with a CFO and show your own benchmark.',
  },
  {
    id: 'b4',
    code: 'B4',
    name: 'VoiceMate',
    tagline: 'Real-time voice agent',
    skill: 'Voice AI, latency engineering',
    whatItIs: 'A customer-support voice agent you can talk to: it listens, responds instantly, handles interruptions and can take actions like checking an order or making a booking.',
    problem: 'Voice is the next front for AI in customer service, but it is unforgiving: latency, interruptions and cost per minute decide whether it is usable.',
    build: 'A LiveKit voice agent; a justified choice between a cascaded speech-to-text → LLM → text-to-speech pipeline and a realtime speech-to-speech model; tool use; barge-in and human review-and-edit patterns; a latency budget and cost tuning; voice safety guardrails.',
    proof: 'End-to-end latency percentiles, task completion, interruption handling, transcription accuracy (WER), cost per minute.',
    ships: 'Deployed voice agent + latency/cost report.',
    stretch: 'Multilingual; call-summary hand-off to TriageOps.',
    why: 'This is the demo everyone remembers — and very few programs teach it.',
  },
  {
    id: 'b5',
    code: 'B5',
    name: 'ShipIt',
    tagline: 'Productionise on AWS',
    skill: 'Cloud deployment, CI/CD, monitoring, handover',
    whatItIs: "Taking an earlier build (AskDocs or DomainCopilot) from 'works on my laptop' to a real, monitored service on AWS — using Amazon Bedrock/AgentCore or SageMaker.",
    problem: 'A prototype is not a product until it is deployed, quality-gated, monitored, costed and handed over to someone else to run.',
    build: 'Containerisation, CI/CD with the eval suite as a deploy gate, observability (traces, metrics, alerts, cost alarms), a rollback plan, an incident fire-drill, a handover document and runbook.',
    proof: 'Deploy-gate pass rate, p95 latency, error/retry rate, monthly cost vs budget, time-to-recover in the fire-drill.',
    ships: "'Deployed on AWS' with dashboards, runbook and cost report.",
    stretch: 'An alternate-stack write-up (GCP Vertex or Azure).',
    why: 'You have deployed and operated an AI system on AWS with monitoring — the line hiring managers look for on a résumé.',
  },
  {
    id: 'b6',
    code: 'B6',
    name: 'RepoAgent',
    tagline: 'Ship a feature into a large codebase with coding agents',
    skill: 'Coding agents, agentic review, evals',
    whatItIs: 'You deliver a real feature in a large, unfamiliar open-source codebase using coding agents (Claude Code / Cursor) as the primary developer — and stay accountable for quality.',
    problem: 'Real engineering happens in big legacy codebases, and agents now do much of the typing. Teams need engineers who can direct agents safely and review their work rigorously.',
    build: 'A spec; planning with the agent; project instruction files (AGENTS.md/CLAUDE.md), hooks and MCP; implementation with calibrated autonomy and safe permissions; agentic code review and an AI security audit; tests and an eval suite for the feature; a merge-quality pull request.',
    proof: 'Tests passing, review findings closed, an eval suite for the feature, time-to-PR, tokens/cost used.',
    ships: 'A PR + eval suite + a retrospective on agent failure modes.',
    stretch: 'Parallel agents on a decomposed task.',
    why: 'Coding agents are taught as a discipline over three sessions and a real project — not a tips-and-tricks webinar.',
  },
];

export const OPTIONAL_BUILD_DETAILS = [
  {
    id: 'o1',
    code: 'O1',
    name: 'AutoClaims Assist',
    tagline: 'Insurance claims triage — regulated vertical',
    skill: 'Regulated multi-agent decisioning',
    scenario: 'A regional insurer wants faster first decisions on auto claims without losing control.',
    build: 'The spec and success metrics; then a multi-agent + MCP + RAG system that reads claim files (reports, photos, repair estimates), surfaces high-confidence first decisions to adjusters, routes edge cases to human review, with audit logging and compliance literacy.',
    proof: 'Decision precision, review-routing recall, audit completeness.',
    why: 'Insurance and BFSI are the biggest buyers of agentic AI in India — this is the build for engineers targeting them.',
  },
  {
    id: 'o2',
    code: 'O2',
    name: 'MedAuth Assist',
    tagline: 'Healthcare prior-authorisation co-pilot — regulated vertical (educational)',
    skill: 'Compliance-first co-pilot',
    scenario: 'A hospital network is stuck at multi-day prior-authorisation turnaround after a compliance-blocked pilot.',
    build: "A spec with compliance controls; a co-pilot that reads the chart note, retrieves the payer's policy, drafts the request with citations and routes ambiguous cases to a reviewer — with PII redaction, audit logs and SSO.",
    proof: 'Citation faithfulness, reviewer agreement, turnaround time.',
    why: 'Shows you can design for compliance from day one — the reason most healthcare AI pilots fail.',
  },
  {
    id: 'o3',
    code: 'O3',
    name: 'CallReview',
    tagline: 'Call-centre intelligence pipeline',
    skill: 'Speech-analytics pipeline',
    scenario: 'QA teams can review under 2% of calls; compliance misses are costly.',
    build: 'Diarised transcription (who said what) → summary → a five-dimension QA scorecard → compliance flags → a downloadable report; batch and streaming modes; cost per call tracked.',
    proof: 'Scorecard agreement with human QA, flag precision/recall, cost per call.',
    why: 'Directly relevant to BPO/contact-centre and GCC employers — a complete speech-analytics pipeline.',
  },
  {
    id: 'o4',
    code: 'O4',
    name: 'QueryPilot',
    tagline: 'Reliable text-to-SQL over a large schema',
    skill: 'Agents over structured data',
    scenario: 'Business users want answers from a 200-table data warehouse without writing SQL — and without wrong numbers.',
    build: 'A semantic layer + schema retrieval (so the agent only sees relevant tables) → SQL generation → sandboxed read-only execution → a verification loop (self-check, assertions) → a charted answer; optional hand-off to a reporting agent.',
    proof: 'Execution accuracy on a golden query set, hallucinated-column rate, latency.',
    why: "The canonical enterprise agent, and one of the most common AI system-design interview questions — you've built it.",
  },
];
